import React, { useContext, useEffect, useState, useCallback } from "react";
import { Task } from "../../../dashboardPage/components/task/Task";
import { ITask } from "../../../../types/dashboard";
import { AppContext } from "../../../../appContext";
import { getTaskItems, postTaskItem, putTaskItem, deleteTaskItem } from "../../../../api/taskApi";
import { addTaskReloadListener, removeTaskReloadListener, triggerTaskReload, addTaskCheckedListener, removeTaskCheckedListener, triggerTaskCheckedUpdate } from "../../../../utils/dataSyncTriggers";
import ChainIcon from '../../../../assets/notesEditor/chain.svg';
import './MeetingPageTasks.css';

interface IMeetingPageTasksProps {
    selectedClientId?: string;
    selectedClientName?: string;
    sessionId?: string;
    sessionDate?: Date | string;
}

export const MeetingPageTasks = ({ selectedClientId, selectedClientName, sessionId, sessionDate }: IMeetingPageTasksProps) => {
    const { translate } = useContext(AppContext);
    const [taskList, setTaskList] = useState<ITask[]>([]);
    const [selectedTaskIndex, setSelectedTaskIndex] = useState<number>(-1);
    const [loading, setLoading] = useState(true);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [filterByClient, setFilterByClient] = useState(false);

    const loadTasks = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getTaskItems();
            if (!res) {
                throw new Error('No response from server');
            }
            const data = await res.json();
            
            let dataArray: any[];
            const reservedKeys = ['message', 'error', 'status', 'success', 'code', 'Error', 'Message'];
            if (Array.isArray(data)) {
                dataArray = data;
            } else {
                dataArray = Object.entries(data)
                    .filter(([id]) => !reservedKeys.includes(id))
                    .map(([id, taskData]) => ({ [id]: taskData }));
            }
            
            const convertedTasks: ITask[] = dataArray.map((item: any) => {
                const id = Object.keys(item)[0];
                if (reservedKeys.includes(id)) return null;
                const taskData = item[id];
                if (!taskData || typeof taskData !== 'object') return null;
                
                const rawDatetime = taskData.Datetime || taskData.datetime;
                let datetime: number | undefined = undefined;
                if (rawDatetime !== undefined && rawDatetime !== null && rawDatetime !== '') {
                    if (typeof rawDatetime === 'number') {
                        datetime = rawDatetime;
                    } else {
                        const date = new Date(rawDatetime);
                        if (!isNaN(date.getTime())) {
                            datetime = Math.floor(date.getTime() / 1000);
                        }
                    }
                }
                
                return {
                    id: id,
                    title: taskData.title || taskData.text?.slice(0, 20) || 'Untitled',
                    text: taskData.text || taskData.body || taskData.description || '',
                    tags: taskData.tags || [],
                    checked: taskData.checked || false,
                    datetime: datetime,
                    hasDate: taskData.hasDate !== undefined ? taskData.hasDate : Boolean(datetime),
                    client: taskData.client,
                    sessionId: taskData.sessionId
                };
            });
            
            // Filter null values and sort tasks: session-assigned tasks first, then others
            const validTasks = convertedTasks.filter(Boolean) as ITask[];
            const sortedTasks = validTasks.sort((a, b) => {
                const aHasSession = a.sessionId === sessionId;
                const bHasSession = b.sessionId === sessionId;
                
                if (aHasSession && !bHasSession) return -1;
                if (!aHasSession && bHasSession) return 1;
                return 0;
            });
            
            setTaskList(sortedTasks);
        } catch (err) {
            console.error('Failed to load tasks:', err);
        } finally {
            setLoading(false);
        }
    }, [sessionId]);

    useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    useEffect(() => {
        addTaskReloadListener(loadTasks);
        
        return () => {
            removeTaskReloadListener(loadTasks);
        };
    }, [loadTasks]);

    useEffect(() => {
        const handleTaskCheckedUpdate = (taskId: string, checked: boolean) => {
            setTaskList(prev => prev.map(t => t.id === taskId ? { ...t, checked } : t));
        };
        addTaskCheckedListener(handleTaskCheckedUpdate);
        return () => {
            removeTaskCheckedListener(handleTaskCheckedUpdate);
        };
    }, []);

    const handleAddTask = async () => {
        const newTask = {
            title: translate('tasks_new_task') || 'New Task',
            text: '',
            tags: [] as string[],
            checked: false,
            Datetime: Math.floor(Date.now() / 1000),
            hasDate: true,
            type: 'task',
            client: filterByClient && selectedClientId ? selectedClientId : undefined
        };

        try {
            const response = await postTaskItem(newTask);
            if (!response) {
                throw new Error('No response from server');
            }
            const result = await response.json();
            console.log('MeetingPageTasks postTaskItem response:', result);
            
            const reservedKeys = ['message', 'error', 'status', 'success', 'code', 'Error', 'Message'];
            const validKeys = Object.keys(result).filter(k => !reservedKeys.includes(k));
            const taskId = result.name || result.id || validKeys[0] || null;
            
            if (!taskId) {
                console.log('No valid task ID from server response, reloading tasks...');
                await loadTasks();
                triggerTaskReload();
                return;
            }
            
            const savedTask: ITask = {
                ...newTask,
                id: taskId,
                datetime: newTask.Datetime
            };

            setTaskList(prev => {
                const updated = [...prev, savedTask];
                setSelectedTaskIndex(updated.length - 1);
                return updated;
            });
            triggerTaskReload();
        } catch (err) {
            console.error('Failed to create task:', err);
        }
    };

    const handleSaveTask = async (index: number, updatedData: { 
        title: string; 
        text: string; 
        tags?: string[]; 
        datetime?: number; 
        hasDate?: boolean;
        client?: string;
        sessionId?: string;
    }) => {
        const task = taskList[index];
        const reservedKeys = ['message', 'error', 'status', 'success', 'code', 'Error', 'Message'];
        if (!task.id || reservedKeys.includes(task.id)) {
            console.error('Cannot save task without valid ID, task.id:', task.id);
            await loadTasks();
            triggerTaskReload();
            return;
        }

        try {
            let finalDatetime = updatedData.datetime;
            let finalHasDate = updatedData.hasDate;
            
            if (updatedData.sessionId && sessionDate) {
                const parsedSessionDate = sessionDate instanceof Date ? sessionDate : new Date(sessionDate);
                if (!isNaN(parsedSessionDate.getTime())) {
                    finalDatetime = Math.floor(parsedSessionDate.getTime() / 1000);
                    finalHasDate = true;
                }
            }
            
            const updatedTask: ITask = {
                ...task,
                title: updatedData.title,
                text: updatedData.text,
                tags: updatedData.tags || [],
                datetime: finalDatetime,
                hasDate: finalHasDate,
                client: updatedData.client,
                sessionId: updatedData.sessionId
            };

            const apiPayload = {
                title: updatedData.title,
                text: updatedData.text,
                tags: updatedData.tags || [],
                Datetime: finalHasDate ? finalDatetime : undefined,
                hasDate: finalHasDate,
                checked: task.checked,
                client: updatedData.client,
                sessionId: updatedData.sessionId,
                type: 'task'
            };

            await putTaskItem(apiPayload, task.id);
            
            setTaskList(prev => prev.map((t, i) => i === index ? updatedTask : t));
            triggerTaskReload();
        } catch (err) {
            console.error('Failed to save task:', err);
        }
    };

    const handleRemoveTask = async (index: number) => {
        const task = taskList[index];
        const reservedKeys = ['message', 'error', 'status', 'success', 'code', 'Error', 'Message'];
        if (!task.id || reservedKeys.includes(task.id)) {
            console.error('Cannot delete task without valid ID');
            await loadTasks();
            triggerTaskReload();
            return;
        }

        try {
            await deleteTaskItem(task.id);
            setTaskList(prev => prev.filter((_, i) => i !== index));
            setSelectedTaskIndex(-1);
            triggerTaskReload();
        } catch (err) {
            console.error('Failed to delete task:', err);
        }
    };

    const handleToggleTask = (index: number) => {
        setSelectedTaskIndex(prev => prev === index ? -1 : index);
    };

    const handleCheckTask = async (index: number) => {
        const task = taskList[index];
        if (!task.id) return;

        const updatedChecked = !task.checked;
        
        setTaskList(prev => prev.map((t, i) => {
            if (i === index) {
                return {...t, checked: updatedChecked};
            }
            return t;
        }));
        triggerTaskCheckedUpdate(task.id, updatedChecked);

        try {
            const apiPayload = {
                title: task.title,
                text: task.text,
                tags: task.tags || [],
                Datetime: task.hasDate ? task.datetime : undefined,
                hasDate: task.hasDate,
                checked: updatedChecked,
                client: task.client,
                sessionId: task.sessionId,
                type: 'task'
            };

            await putTaskItem(apiPayload, task.id);
        } catch (err) {
            console.error('Failed to update task check status:', err);
            setTaskList(prev => prev.map((t, i) => {
                if (i === index) {
                    return {...t, checked: !updatedChecked};
                }
                return t;
            }));
            triggerTaskCheckedUpdate(task.id, !updatedChecked);
        }
    };

    const filteredTaskList = filterByClient && selectedClientId
        ? taskList.filter(task => task.client === selectedClientId)
        : taskList;

    return (
        <div className={`meetingPageTasks ${isCollapsed ? 'meetingPageTasks--collapsed' : ''}`}>
            <div className="meetingPageTasks_header" onClick={() => setIsCollapsed(!isCollapsed)}>
                <div className="meetingPageTasks_title_wrapper">
                    <div className="meetingPageTasks_title">
                        {translate('tasks_title') || 'Tasks'}
                    </div>
                    {selectedClientId && selectedClientName && (
                        <div 
                            className={`meetingPageTasks_filter_icon ${!filterByClient ? 'meetingPageTasks_filter_icon--active' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setFilterByClient(!filterByClient);
                            }}
                            title={filterByClient 
                                ? translate('filter_show_client_tasks').replace('{name}', selectedClientName)
                                : translate('filter_show_all_tasks')
                            }
                        />
                    )}
                </div>
                <div className="meetingPageTasks_toggle">
                    <div className="meetingPageTasks_toggle_icon"></div>
                </div>
            </div>
            <div className="meetingPageTasks_drop">
                <div className="meetingPageTasks_drop_content">
                    {loading ? (
                        <div className="meetingPageTasks_loading">
                            {translate('common_loading') || 'Loading...'}
                        </div>
                    ) : (
                        <div className="meetingPageTasks_content">
                            <div className="meetingPageTasks_list">
                                {filteredTaskList.map((task, index) => (
                                    <Task 
                                        key={task.id || index}
                                        isOpen={index === selectedTaskIndex} 
                                        data={task} 
                                        onToggle={() => handleToggleTask(index)} 
                                        onCheck={() => handleCheckTask(index)}
                                        onRemove={() => handleRemoveTask(index)}
                                        onSave={(updatedData) => handleSaveTask(index, updatedData)}
                                        minimal={true}
                                        selectedClientId={selectedClientId}
                                        selectedClientName={selectedClientName}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="meetingPageTasks_actions">
                        <div className="meetingPageTasks_add_wrapper" onClick={handleAddTask}>
                            <div className="meetingPageTasks_add_icon"></div>
                            <span className="meetingPageTasks_add_text">
                                {translate('tasks_add_new') || 'Add a new task'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
